import { UiHeroScrollFxController } from "../sections/UiHeroScrollFxController";
import { HomeLocationController } from "../sections/HomeLocationController";
import { SelectWrapper } from "../../components/SelectWrapper";
import { Form } from "../../core/Form";
import * as Modal from "../../utils/Modal";

export const HomeController = {
    index(): void {
        UiHeroScrollFxController.init();
        initContactSelects();
        initMainContactForm();
    },
    contact(): void {
        initContactSelects();
        initMainContactForm();
        HomeLocationController.init();
    },
};

/**
 * 메인 Contact 폼의 네이티브 <select> 를 SelectWrapper(→Select.ts 커스텀 드롭다운)로 업그레이드.
 * #2 래퍼 규칙: 외부/내장 select 를 직접 다루지 않고 프로젝트 정본 래퍼(SelectWrapper)만 사용.
 * Select.ts 가 dataset.csInited 로 중복 init 을 방어하므로 재호출 안전.
 */
function initContactSelects(): void {
    document
        .querySelectorAll<HTMLSelectElement>(".sec-theh-contact [data-theh-select]")
        .forEach((el) => {
            // data-searchable="false" → 검색 끔 / "true" → 검색 켬 / 미지정 → 자동(옵션 5개 초과 시 표시)
            const ds = el.dataset.searchable;
            const searchable = ds === undefined ? undefined : ds !== "false";
            new SelectWrapper(el, {
                placeholder: el.dataset.placeholder,
                searchPlaceholder: el.dataset.searchPlaceholder,
                searchable,
            }).init();
        });
}

function initMainContactForm(): void {
    const form = document.querySelector<HTMLFormElement>("[data-main-contact-form]");
    if (!form) return;
    if (form.dataset.mainContactBound === "1") return;
    form.dataset.mainContactBound = "1";
    initPhoneFormatter(form);
    initContactConsentValidation(form);

    new Form(form, {
        loaderSelector: "[data-submit-loader]",
        submittingText: "문의 접수 중…",
        onSuccess: async (data) => {
            const message = typeof data.message === "string"
                ? data.message
                : typeof data.msg === "string"
                    ? data.msg
                : "문의가 정상적으로 접수되었습니다. 감사드립니다. 확인 후 빠르게 연락드리겠습니다.";
            form.reset();
            await Modal.alert(message, "문의 접수 완료", { variant: "success" });
        },
        onFail: async (message) => {
            const errorMessage = message || "접수 중 오류가 발생했습니다. 입력 내용을 확인한 뒤 다시 시도해 주세요.";
            await Modal.alert(errorMessage, "문의 접수 실패", { variant: "error" });
        },
        requestFailedMessage: "네트워크 오류로 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.",
    }).init();
}

function formatPhoneNumber(value: string): string {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return digits;

    if (digits.startsWith("02")) {
        const rest = digits.slice(2);
        if (rest.length <= 3) return `02-${rest}`;
        if (rest.length <= 7) return `02-${rest.slice(0, 3)}-${rest.slice(3, 7)}`.replace(/-$/, "");
        return `02-${rest.slice(0, 4)}-${rest.slice(4, 8)}`.replace(/-$/, "");
    }

    const area = digits.slice(0, 3);
    const rest = digits.slice(3);
    if (rest.length <= 4) return `${area}-${rest}`;
    return `${area}-${rest.slice(0, 4)}-${rest.slice(4, 8)}`.replace(/-$/, "");
}

function digitCount(value: string): number {
    return (value.match(/\d/g) || []).length;
}

function cursorPositionForDigits(value: string, digitsBefore: number): number {
    if (digitsBefore <= 0) return 0;
    let seen = 0;
    for (let i = 0; i < value.length; i += 1) {
        if (!/\d/.test(value[i])) continue;
        seen += 1;
        if (seen >= digitsBefore) return i + 1;
    }
    return value.length;
}

function initPhoneFormatter(form: HTMLFormElement): void {
    const phone = form.querySelector<HTMLInputElement>('input[name="phone"][data-phone-format="1"]');
    if (!phone || phone.dataset.phoneFormatBound === "1") return;
    phone.dataset.phoneFormatBound = "1";

    phone.addEventListener("keydown", (event) => {
        if (event.key !== "Backspace" && event.key !== "Delete") return;
        const start = phone.selectionStart ?? 0;
        const end = phone.selectionEnd ?? start;
        if (start !== end) return;

        const separatorIndex = event.key === "Backspace" ? start - 1 : start;
        if (separatorIndex < 0 || phone.value[separatorIndex] !== "-") return;

        // 마스크 구분선 자체는 삭제할 수 없으므로, 사용자가 기대하는 인접 숫자를 삭제한다.
        event.preventDefault();
        const digits = phone.value.replace(/\D/g, "").split("");
        const digitIndex = event.key === "Backspace"
            ? digitCount(phone.value.slice(0, start)) - 1
            : digitCount(phone.value.slice(0, start));
        if (digitIndex < 0 || digitIndex >= digits.length) return;
        digits.splice(digitIndex, 1);

        const formatted = formatPhoneNumber(digits.join(""));
        phone.value = formatted;
        const caret = cursorPositionForDigits(formatted, digitIndex);
        phone.setSelectionRange(caret, caret);
    });

    phone.addEventListener("input", () => {
        const start = phone.selectionStart ?? phone.value.length;
        const end = phone.selectionEnd ?? start;
        const startDigits = digitCount(phone.value.slice(0, start));
        const endDigits = digitCount(phone.value.slice(0, end));
        const formatted = formatPhoneNumber(phone.value);
        if (phone.value === formatted) return;

        phone.value = formatted;
        if (document.activeElement === phone) {
            phone.setSelectionRange(
                cursorPositionForDigits(formatted, startDigits),
                cursorPositionForDigits(formatted, endDigits),
            );
        }
    });
}

function initContactConsentValidation(form: HTMLFormElement): void {
    const consent = form.querySelector<HTMLInputElement>("#contact-privacy-agree");
    const error = form.querySelector<HTMLElement>("#contact-privacy-error");
    if (!consent || !error || consent.dataset.consentValidationBound === "1") return;
    consent.dataset.consentValidationBound = "1";

    const syncError = (visible: boolean): void => {
        error.hidden = !visible;
        consent.setAttribute("aria-invalid", visible ? "true" : "false");
        consent.closest(".no-form-checkbox")?.classList.toggle("is-invalid", visible);
    };

    consent.addEventListener("invalid", (event) => {
        event.preventDefault();
        syncError(true);
        consent.focus({ preventScroll: true });
        error.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    consent.addEventListener("change", () => syncError(false));
}
